// @ts-nocheck
import { useNotifierAction } from "@lib/notification";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { useTheme } from "@mui/styles";
import * as Sentry from "@sentry/nextjs";
import { appInitialLoad, getPresignedUrl } from "@services/app";
import {
  deleteImages,
  fetchFavoriteIds,
  getImageLibrary,
  postImageFavorite,
  postImages,
} from "@services/dashboard";
import {
  fetchTemplate,
  getImagexPreview,
  postProducts,
  useImageTags,
} from "@services/template";
import type { RootState } from "@stores/store";
import animateScrollTo from "animated-scroll-to";
import dynamic from "next/dynamic";
import Router from "next/router";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/zoom";

const Desktop = dynamic(
  () => import("@xcustomizer/packages/studio").then((mod) => mod.Desktop),
  {
    loading: () => (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    ),
  }
);

const LoginDialog = dynamic(() => import("./loginDialog"));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide ref={ref} {...props} direction="up" />;
});

function Customizer(props) {
  const { template } = props;
  const [isLoginDialogOpen, setLoginDialogOpen] = React.useState(false);
  const [data, setData] = useState(null);
  const theme: Theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [, setCustomizer] = React.useState(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const { data: imageTags } = useImageTags();

  React.useEffect(() => {
    if (template && imageTags) {
      setIsLoaded(false);
    }
  }, [template, imageTags]);

  const app = useSelector((store: RootState) => store.app);
  const user = app.user || null;

  const { enqueueSnackbar } = useNotifierAction();

  React.useEffect(() => {
    if (open && !isLoaded && imageTags) {
      setIsLoaded(true);
    }
  }, [open, imageTags]);

  useEffect(() => {
    if (imageTags) {
      let tmp = {
        initCategoryUrl: "http://127.0.0.1:4000/api/v2/app/categories",
        pickerDataUrl:
          "https://launchard.s3-us-west-1.amazonaws.com/resources/customizer/picker-data.json",
        template: {
          id: template.id,
          title: template.title,
          description: template.app_description,
          initAreas: template.initAreas,
          ruler: 1000,
          image: template.image,
          files: template.files,
          images: template.images,
          scenes: template.scenes,
          colors: template.colors,
        },
        image_tags: imageTags,
      };
      setData(tmp);
    }
  }, [imageTags, template]);

  const openLoginDialog = () => {
    setLoginDialogOpen(true);
  };

  const onClose = () => {
    setLoginDialogOpen(false);
  };

  const getImageWidthHeight = (file, resolve) => {
    let data = {
      width: 0,
      height: 0,
    };
    //读取图片数据
    const reader = new FileReader();
    reader.onload = function (e) {
      let result = e.target.result;
      //加载图片获取图片真实宽度和高度
      const image = new Image();
      image.onload = function () {
        data.width = image.width;
        data.height = image.height;
        resolve(data);
      };
      // @ts-ignore
      image.src = result;
    };
    reader.readAsDataURL(file);
  };

  const getSignedUrl = (file, callback) => {
    const params = {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
    };

    // /signing/server
    new Promise(function (resolve, reject) {
      getImageWidthHeight(file, resolve);
    })
      .then(function (result) {
        let data = Object.assign(params, result);
        getPresignedUrl(data).then((res) => {
          const { data } = res;
          callback && callback(data.data);
        });
      })
      .catch(function (reason) {
        // console.log("Failed: " + reason);
      });
  };

  const getImagex = (design, callback) => {
    getImagexPreview(design, { responseType: "blob" })
      .then((res) => {
        const { data } = res;
        let url = (window.URL || window.webkitURL).createObjectURL(data.data);
        callback && callback(url, design);
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  const getTemplate = (templateId, callback, onError) => {
    fetchTemplate({ id: templateId })
      .then((res) => {
        const data = res.data;
        callback && callback(data["template"]);
      })
      .catch((error) => {
        onError && onError(error);
      });
  };

  const saveDesign = (designs, callback, onError) => {
    if (!designs) {
      callback && callback();
      setOpen(false);
      return false;
    }
    appInitialLoad().then((res) => {
      const data = res;
      if (!data.user) {
        openLoginDialog();
        onError();
      } else {
        animateScrollTo(0);
        postProducts(designs)
          .then((res) => {
            const data = res.data;
            if (data["products"]) {
              // 暂时只能上传一个产品
              Router.push(
                `/product?id=${data.products[0].id}`,
                `/product/${data.products[0].id}`
              );
              // callback && callback(data["products"]);
            }
          })
          .catch((error) => {
            const data = error && error.response && error.response.error;
            Sentry.captureMessage(
              JSON.stringify({
                user_id: app.user.id,
                template_id: template.id,
                errorMessage: data ? data : {},
              })
            );
            onError && onError(error);
          });
      }
    });
  };

  const getImages = (page, query, callback, onError) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    return getImageLibrary(Object.assign({ page }, query), {
      signal: abortControllerRef.current.signal,
    })
      .then((res) => {
        callback && callback(res);
      })
      .catch((error) => {
        onError && onError(error);
      });
  };

  const postImage = (file, callback, onError) => {
    postImages({
      file_name: file.originName,
      type: file.contentType,
      extension: file.ext,
      width: file.width,
      height: file.height,
      size: file.fileSize,
      src: file.src,
    })
      .then((res) => {
        const data = res.data;
        callback && callback(data.image);
      })
      .catch((error) => {
        onError && onError(error);
      });
  };

  const deleteImage = (image, callback, onError) => {
    deleteImages({
      ids: [image.id],
    })
      .then(() => {
        callback && callback();
      })
      .catch(() => {
        onError && onError();
      });
  };

  const notification = (type, message) => {
    enqueueSnackbar({
      message: message,
      options: {
        key: new Date().getTime() + Math.random(),
        variant: type,
      },
    });
  };

  const handleClick = () => {
    setOpen(true);
  };

  const onRef = (instance) => {
    setCustomizer(instance);
  };

  const setFavorite = (id, method: "ADD" | "REMOVE") => {
    return postImageFavorite({ id, method });
  };

  const getFavoriteIds = () => {
    return fetchFavoriteIds();
  };

  return (
    <Box
      sx={{
        display: "flex",
      }}
    >
      <Button
        variant="contained"
        fullWidth
        sx={{ height: 48, fontSize: 18 }}
        onClick={handleClick}
        id="design-start-design-button"
        disabled={template.status === "SOLD_OUT"}
      >
        {template.status === "SOLD_OUT" ? "Sold Out" : "Start Design"}
      </Button>
      <Dialog
        open={open}
        keepMounted={true}
        fullScreen
        TransitionComponent={Transition}
      >
        {isLoaded && (
          <Desktop
            onRef={onRef}
            user={user}
            initData={data}
            getSignedUrl={getSignedUrl}
            getImagex={getImagex}
            getTemplate={getTemplate}
            onExit={saveDesign}
            getImages={getImages}
            deleteImage={deleteImage}
            postImage={postImage}
            setFavorite={setFavorite}
            getFavoriteIds={getFavoriteIds}
            previewDelay={500}
            notification={notification}
            theme={{
              fontFamily: theme.typography.fontFamily,
              palette: {
                primary: {
                  lighter: "#e0f2f1",
                  // lighter: lighten(theme.palette.primary.lighter, 0.5),
                  light: theme.palette.primary.light,
                  main: theme.palette.primary.main,
                  dark: theme.palette.primary.dark,
                  // @ts-ignore
                  darker: theme.palette.primary.darker,
                },
                secondary: theme.palette.secondary,
                grey: "#f2f2f2",
                text: {
                  light: "#8d9095",
                  main: "#40354e",
                },
                border: "#e4e4e4",
              },
            }}
          />
        )}
      </Dialog>

      {isLoaded && <LoginDialog open={isLoginDialogOpen} onClose={onClose} />}
    </Box>
  );
}

export default Customizer;
